type Endpoint =
  | "matches"
  | "match"
  | "matchStats"
  | "matchLineups"
  | "standings"
  | "commentary";

const TTL_MS: Record<
  Endpoint,
  { settled: number; warmup: number; live: number; cooldown: number }
> = {
  matches:      { settled: 86_400_000, warmup: 3_600_000, live:  30_000, cooldown: 3_600_000 },
  match:        { settled: 86_400_000, warmup: 3_600_000, live:  30_000, cooldown: 3_600_000 },
  matchStats:   { settled: 86_400_000, warmup: 3_600_000, live:  30_000, cooldown: 3_600_000 },
  matchLineups: { settled: 86_400_000, warmup: 3_600_000, live: 180_000, cooldown: 3_600_000 },
  standings:    { settled: 86_400_000, warmup: 3_600_000, live:  30_000, cooldown: 3_600_000 },
  commentary:   { settled: 86_400_000, warmup: 3_600_000, live:  30_000, cooldown: 3_600_000 },
};

const HOUR = 3_600_000;

/**
 * Returns the next refetch interval for an endpoint, clamped so the FE
 * crosses each phase boundary (KO−24h, KO−1h, KO+4h, KO+8h) within ~1s.
 * The interval is recomputed each tick by TanStack's refetchInterval callback,
 * so as `now` approaches a boundary the next tick shrinks naturally.
 */
export function pollIntervalMs(endpoint: Endpoint, kickoff?: string): number {
  const phases = TTL_MS[endpoint];
  if (!kickoff) return phases.live; // pessimistic when unknown
  const ko = new Date(kickoff).getTime();
  if (Number.isNaN(ko)) return phases.live;
  const dMs = Date.now() - ko;

  let phaseTTL: number;
  let msUntilNextBoundary: number;
  if (dMs >= -HOUR && dMs <= 4 * HOUR) {
    phaseTTL = phases.live;
    msUntilNextBoundary = 4 * HOUR - dMs;
  } else if (dMs > 4 * HOUR && dMs <= 8 * HOUR) {
    phaseTTL = phases.cooldown;
    msUntilNextBoundary = 8 * HOUR - dMs;
  } else if (dMs >= -24 * HOUR && dMs < -HOUR) {
    phaseTTL = phases.warmup;
    msUntilNextBoundary = -HOUR - dMs;
  } else if (dMs < -24 * HOUR) {
    phaseTTL = phases.settled;
    msUntilNextBoundary = -24 * HOUR - dMs;
  } else {
    phaseTTL = phases.settled;
    msUntilNextBoundary = Number.POSITIVE_INFINITY;
  }

  return Math.max(1_000, Math.min(phaseTTL, msUntilNextBoundary));
}

/**
 * For /soccer/matches list — drives phase from the closest-to-now match.
 */
export function closestKickoff(
  matches?: Array<{ matchDateTime: string }>
): string | undefined {
  if (!matches?.length) return undefined;
  const now = Date.now();
  let best: { dt: string; abs: number } | undefined;
  for (const m of matches) {
    const t = new Date(m.matchDateTime).getTime();
    if (Number.isNaN(t)) continue;
    const abs = Math.abs(t - now);
    if (!best || abs < best.abs) best = { dt: m.matchDateTime, abs };
  }
  return best?.dt;
}
